import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import {
  DEFAULT_SHIPPING_TAB,
  ORIGINS,
  ORIGIN_LABEL,
  SHIPPING_GROUPS,
  canDispatch,
  hasLeft,
  isReleased,
  isOrigin,
  matchesSearch,
  shippingGroup,
  shippingTabOf,
  type JobOrder,
  type ShippingGroup,
} from '@/domain';
import {
  useDispatchOrder,
  useHoldOrder,
  useOrdersInHand,
  useReleaseHold,
  useUpdateShipping,
} from '@/application/usecases/useOrders';
import { useTableOrder } from '@/application/usecases/useTableOrder';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { formatDay } from '@/shared/format/instant';
import { Button } from '@/ui/primitives/Button';
import { Tabs } from '@/ui/primitives/Tabs';
import { FilterBar, type FilterDef } from '@/ui/data/FilterBar';
import { SortSelect } from '@/ui/data/SortSelect';
import { ORIGIN_SORT_KEYS, casePresets } from '@/ui/data/casePresets';
import { toast } from '@/ui/primitives/toastStore';
import { HoldNote, StatusDot } from '@/ui/data/StatusDot';
import { ShippingBadge } from '@/ui/data/ShippingBadge';
import { ShippingDates } from '@/ui/data/ShippingDates';
import { ShipIcon } from '@/ui/icons';
import { EmptyGroup, GroupLabel, OrderRow, orderRowStyles } from '../orders/OrderRow';
import { HoldDialog } from '../orders/HoldDialog';
import { DispatchDialog } from '../orders/DispatchDialog';
import { TrackingDialog } from '../orders/TrackingDialog';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { useMarkReceived } from './useMarkReceived';
import { ShippingFigures } from './ShippingFigures';
import { SHIPPING_TAB_EMPTY, SHIPPING_TAB_LABEL } from './shippingTabs';
import screen from '../orders/OrdersScreen.module.css';
import { SkeletonRows } from '@/ui/feedback/Loading';

/**
 * Which dialog is open, and for which order. One at a time. Mark received is
 * not among them: its confirmation is `useMarkReceived`'s, shared with the
 * archive and the case record.
 */
type Open =
  | { kind: 'dispatch' | 'hold'; jobOrderNo: string }
  | { kind: 'tracking' | 'edit'; order: JobOrder }
  | null;

/** Where the screen keeps its tab, so a figure elsewhere or a link can open one. */
const TAB_PARAM = 'tab';

/**
 * Shipping and dispatch. Section 5.2, and the request of 13 September 2026,
 * section 1.6.
 *
 * A tab for each place a case stands between its work being finished and the
 * customer having it, in the order it passes through them: waiting for
 * Accounts, ready to dispatch, awaiting its waybill code, in transit, received,
 * and On hold, which stops a case at any point before it leaves. Over the tabs,
 * the figures for every one of them, each opening its tab. The lab's request
 * of 22 October 2026: the groups were stacked down one page before, with a
 * switch to hide the two nobody at the desk acts on.
 *
 * A held order is never OFFERED for dispatch, and the server refuses it too:
 * hiding the button is not the same as making dispatch impossible, which is
 * why section 7.10 item 9 exists.
 *
 * Since the request of 5 October 2026 (third), section 2, nothing here is
 * applied before it is confirmed: Dispatch, Add tracking number, Edit and Hold
 * each open a dialog that is its own confirmation, and Release, the one action
 * that was a single tap, asks in the shared one.
 */
export function ShippingScreen() {
  const { data, isPending, isError } = useOrdersInHand();
  const dispatch = useDispatchOrder();
  const hold = useHoldOrder();
  const release = useReleaseHold();
  const updateShipping = useUpdateShipping();
  // The question, the mark and the Undo after it. The request of 5 October
  // 2026 (third), section 11.
  const received = useMarkReceived();
  const ask = useConfirmAction();

  const [open, setOpen] = useState<Open>(null);
  const close = () => setOpen(null);

  // The tab, in the address: the screen opens on Ready to dispatch.
  const [params, setParams] = useSearchParams();
  const tab = shippingTabOf(params.get(TAB_PARAM));
  const chooseTab = (next: ShippingGroup) =>
    setParams(
      (current) => {
        const address = new URLSearchParams(current);
        if (next === DEFAULT_SHIPPING_TAB) address.delete(TAB_PARAM);
        else address.set(TAB_PARAM, next);
        return address;
      },
      { replace: true },
    );

  /*
   * The bar over the list. Section 8.1 of the request of 16 September 2026:
   * the screen is cards rather than a table, so it has never had either a
   * search or an order of its own. Kept from tab to tab.
   *
   * All of it in memory, because the screen already fetches every case it shows.
   */
  const [query, setQuery] = useState('');
  const [carrier, setCarrier] = useState('all');
  // Request of 3 October 2026, section 2. Kept while the desk works the page,
  // through every dialog and every case that moves from one tab to another.
  const [origin, setOrigin] = useState('all');

  /*
   * Oldest first is the default: the case that has been waiting longest for a
   * waybill is the one to telephone the courier about. Newest first, Urgent
   * first and Doctor A to Z are the other three that mean anything here; a
   * dispatch desk has no bench time to sort by.
   */
  const presets = useMemo(
    () =>
      casePresets<JobOrder>([
        // Newest first unless the reader chose otherwise. Request of 5 October 2026, section 2.
        'newestFirst',
        'oldestFirst',
        'urgentFirst',
        'doctorAtoZ',
        // Sorting by origin, request of 3 October 2026, section 2.
        ...ORIGIN_SORT_KEYS,
      ]),
    [],
  );
  const order = useTableOrder('shipping', presets);

  const today = formatDay(new Date().toISOString());
  const orders = data?.items ?? [];
  const inGroup = (group: ShippingGroup) =>
    orders.filter((candidate) => shippingGroup(candidate, today, formatDay) === group);

  /** Every courier that actually carried something, which is what is worth filtering by. */
  const carriers = useMemo(
    () =>
      // Read inside the callback: `data?.items ?? []` outside it is a new array
      // on every render, which would make this memo do nothing at all.
      [
        ...new Set(
          (data?.items ?? [])
            .map((candidate) => candidate.dispatch.carrier)
            .filter((name): name is string => name !== null && name !== ''),
        ),
      ].sort((a, b) => a.localeCompare(b)),
    [data],
  );

  // A courier means something only on a tab whose cases have left.
  const byCarrier = hasLeft(tab);
  const filtering = query.trim() !== '' || (byCarrier && carrier !== 'all') || origin !== 'all';

  /** Every filter but the origin, which is what the origin's counts are counted under. */
  const matchesOthers = (candidate: JobOrder) =>
    // The doctor and patient names are ABSENT for a caller without seeIdentity,
    // and an absent field simply never matches.
    matchesSearch(query, [
      candidate.jobOrderNo,
      candidate.dentistName,
      candidate.patientName,
      candidate.dispatch.carrier,
      candidate.dispatch.trackingCode,
    ]) && (!byCarrier || carrier === 'all' || candidate.dispatch.carrier === carrier);

  const matches = (candidate: JobOrder) =>
    matchesOthers(candidate) && (origin === 'all' || candidate.origin === origin);

  /*
   * The origins the cases of this tab actually come from, in the client's
   * order, each with how many cases it has under the other filters. Read off
   * the cases rather than a fixed list, so an origin with nothing here is not
   * offered, and one the lab adds appears the day a case arrives from it. The
   * one chosen stays offered when a search empties it.
   */
  const onTab = inGroup(tab);
  const originCounts = new Map<string, number>();
  for (const candidate of onTab) {
    const count = originCounts.get(candidate.origin) ?? 0;
    originCounts.set(candidate.origin, count + (matchesOthers(candidate) ? 1 : 0));
  }
  if (origin !== 'all' && !originCounts.has(origin)) originCounts.set(origin, 0);
  const originRankOf = (value: string) => {
    const index = (ORIGINS as readonly string[]).indexOf(value);
    return index < 0 ? ORIGINS.length : index;
  };
  const originOptions = [...originCounts.entries()].sort(
    ([a], [b]) => originRankOf(a) - originRankOf(b) || a.localeCompare(b),
  );

  /*
   * The case the dispatch dialog is open for, as the list holds it: the dialog
   * says where it is going, which the case carries (`deliverTo`). The request
   * of 6 October 2026 (fifth), section 2.
   */
  const dispatching =
    open?.kind === 'dispatch'
      ? orders.find((candidate) => candidate.jobOrderNo === open.jobOrderNo)
      : undefined;

  /** The tab's cases as they are read: filtered, then in the order the desk chose. */
  const shown = order.apply(onTab.filter(matches));

  /** Each tab's figure: every case in it, before anything is typed into the search. */
  const counts = Object.fromEntries(
    SHIPPING_GROUPS.map((group) => [group, isError ? null : isPending ? undefined : inGroup(group).length]),
  ) as Record<ShippingGroup, number | null | undefined>;

  const filters: FilterDef[] = [
    {
      kind: 'search',
      label: strings.shipping.search,
      placeholder: strings.shipping.searchPlaceholder,
      value: query,
      onChange: setQuery,
    },
    ...(byCarrier
      ? [
          {
            kind: 'select' as const,
            label: strings.shipping.carrier,
            value: carrier,
            options: [
              { value: 'all', label: strings.shipping.allCarriers },
              ...carriers.map((name) => ({ value: name, label: name })),
            ],
            onChange: setCarrier,
          },
        ]
      : []),
    {
      kind: 'select',
      label: strings.shipping.origin,
      value: origin,
      options: [
        { value: 'all', label: strings.shipping.allOrigins },
        ...originOptions.map(([value, count]) => ({
          value,
          label: `${isOrigin(value) ? ORIGIN_LABEL[value] : value} (${count})`,
        })),
      ],
      onChange: setOrigin,
    },
  ];

  const editButton = (order: JobOrder) => (
    <Button onClick={() => setOpen({ kind: 'edit', order })}>{strings.shipping.edit}</Button>
  );

  const holdButton = (order: JobOrder) => (
    <Button onClick={() => setOpen({ kind: 'hold', jobOrderNo: order.jobOrderNo })}>
      {strings.payments.hold}
    </Button>
  );

  /*
   * On every case that has left and that nobody has marked: the ones on their
   * way, and the ones still waiting for a waybill code too, because a parcel
   * can reach the customer before its code reaches the desk. The request of
   * 5 October 2026 (third), section 11.
   */
  const receivedButton = (order: JobOrder) =>
    received.allowed && (
      <Button
        onClick={() =>
          received.ask({
            jobOrderNo: order.jobOrderNo,
            rowVersion: order.rowVersion,
            dispatchedAt: order.dispatch.dispatchedAt,
          })
        }
      >
        {strings.shipping.markReceived}
      </Button>
    );

  /** How it went: the courier and its code, or the lab's van and who drove it. */
  const route = (order: JobOrder) =>
    order.dispatch.method === 'lab' ? (
      <span className={orderRowStyles.carrier}>
        <ShipIcon /> {strings.shipping.labDelivery}
        {order.dispatch.driverName !== null && ` · ${order.dispatch.driverName}`}
      </span>
    ) : (
      <>
        <span className={orderRowStyles.carrier}>
          <ShipIcon /> {order.dispatch.carrier}
        </span>
        <span className={orderRowStyles.tracking}>
          {order.dispatch.trackingCode ?? strings.shipping.noCodeYet}
        </span>
      </>
    );

  /** What a case in each tab can have done to it, beside where it stands. */
  const actions: Record<ShippingGroup, (order: JobOrder) => ReactNode> = {
    /*
     * Shown so the desk sees what is coming, and held from here if it must be:
     * Accounts lets it go by marking it paid or releasing it.
     */
    awaiting_payment: (order) => (
      <>
        <StatusDot tone="watch">{strings.shipping.readyAwaitingPayment}</StatusDot>
        {holdButton(order)}
      </>
    ),
    ready: (order) => (
      <>
        {/* Where the case stands, in the same words every other screen uses for it. Section 7. */}
        <ShippingBadge order={order} />
        {/* Let go by Accounts before it is paid: the desk sends it all the same. 17 October 2026. */}
        {!order.payment.paid && isReleased(order) && (
          <StatusDot tone="watch">{strings.shipping.releasedUnpaid}</StatusDot>
        )}
        {holdButton(order)}
        <Button
          variant="primary"
          icon={<ShipIcon />}
          // Belt and braces with the tab: only a cleared order is dispatchable, and the domain says which.
          disabled={!canDispatch(order.state)}
          onClick={() => setOpen({ kind: 'dispatch', jobOrderNo: order.jobOrderNo })}
        >
          {strings.shipping.dispatch}
        </Button>
      </>
    ),
    awaiting_tracking: (order) => (
      <>
        <ShippingBadge order={order} />
        {route(order)}
        {/* How long it has been waiting, which is the figure that says when to telephone the courier. Section 7.2. */}
        <ShippingDates dispatch={order.dispatch} />
        <Button variant="primary" onClick={() => setOpen({ kind: 'tracking', order })}>
          {strings.shipping.addTracking}
        </Button>
        {receivedButton(order)}
        {editButton(order)}
      </>
    ),
    in_transit: (order) => (
      <>
        <ShippingBadge order={order} />
        {route(order)}
        <ShippingDates dispatch={order.dispatch} />
        {receivedButton(order)}
        {editButton(order)}
      </>
    ),
    delivered: (order) => (
      <>
        <ShippingBadge order={order} />
        <ShippingDates dispatch={order.dispatch} />
        {order.dispatch.receivedBy !== null && (
          <span className={orderRowStyles.carrier}>
            {strings.shipping.receivedBy} {order.dispatch.receivedBy}
          </span>
        )}
        {editButton(order)}
      </>
    ),
    // No dispatch button here, at any price.
    held: (order) => (
      <>
        <HoldNote>{order.hold.reason ?? strings.payments.onHold}</HoldNote>
        <ShippingBadge order={order} />
        <Button
          disabled={release.isPending}
          onClick={() =>
            ask(
              strings.confirm.releaseHold(order.jobOrderNo),
              () =>
                release.mutateAsync(order.jobOrderNo).then(() => {
                  toast(`${order.jobOrderNo} ${strings.payments.holdReleased}`);
                }),
              { confirmLabel: strings.payments.releaseHold },
            )
          }
        >
          {strings.payments.releaseHold}
        </Button>
      </>
    ),
  };

  /** What a tab says under its heading, where it needs saying. */
  const tabHint: Partial<Record<ShippingGroup, string>> = {
    awaiting_payment: strings.shipping.waitingHint,
    delivered: strings.shipping.receivedHint,
  };

  return (
    <div>
      <div className={screen.head}>
        <span className={screen.lbl}>{strings.shipping.label}</span>
        <h2>{strings.shipping.title}</h2>
        <span className={screen.hint}>{strings.shipping.hint}</span>
      </div>

      <ShippingFigures counts={counts} current={tab} onSelect={chooseTab} />

      <Tabs
        label={strings.shipping.tabsLabel}
        current={tab}
        onSelect={chooseTab}
        tabs={SHIPPING_GROUPS.map((group) => ({ key: group, label: SHIPPING_TAB_LABEL[group] }))}
      >
        <FilterBar
          label={strings.shipping.filtersLabel}
          filters={filters}
          sort={<SortSelect presets={presets} value={order.presetKey} onChange={order.choose} />}
        />

        {isPending && <SkeletonRows rows={5} label={strings.common.loadingOrders} />}
        {isError && <p className={screen.hint}>{strings.errors.server}</p>}

        {!isPending && !isError && (
          <>
            {/* The tab's name and how many it holds as filtered: the strip scrolls on a phone. */}
            <GroupLabel count={shown.length}>{SHIPPING_TAB_LABEL[tab]}</GroupLabel>
            {tabHint[tab] !== undefined && <p className={screen.hint}>{tabHint[tab]}</p>}
            {shown.length === 0 ? (
              <EmptyGroup>{filtering ? strings.shipping.noneMatch : SHIPPING_TAB_EMPTY[tab]}</EmptyGroup>
            ) : (
              shown.map((candidate) => (
                <OrderRow
                  key={candidate.jobOrderNo}
                  order={candidate}
                  // Context rather than work: the received, and what Accounts have still to let go.
                  muted={tab === 'delivered' || tab === 'awaiting_payment'}
                  actions={actions[tab](candidate)}
                />
              ))
            )}
          </>
        )}
      </Tabs>

      {open?.kind === 'dispatch' && (
        <DispatchDialog
          jobOrderNo={open.jobOrderNo}
          deliverTo={dispatching?.deliverTo}
          doctorName={dispatching?.dentistName}
          busy={dispatch.isPending}
          onCancel={close}
          onConfirm={(input) =>
            dispatch.mutate(
              { jobOrderNo: open.jobOrderNo, ...input },
              {
                onSuccess: () => {
                  toast(
                    input.method === 'lab'
                      ? `${open.jobOrderNo} ${strings.shipping.outForDelivery}`
                      : `${open.jobOrderNo} ${strings.shipping.dispatchedVia} ${input.carrier ?? ''}`,
                  );
                  close();
                },
                // A 409 here is the server refusing a held or unready order.
                // The detail says which, verbatim.
                onError: (error: Error) => toast(failureWords(error)),
              },
            )
          }
        />
      )}

      {open?.kind === 'tracking' && (
        <TrackingDialog
          jobOrderNo={open.order.jobOrderNo}
          carrier={open.order.dispatch.carrier}
          busy={updateShipping.isPending}
          onCancel={close}
          onConfirm={(trackingCode) =>
            updateShipping.mutate(
              {
                jobOrderNo: open.order.jobOrderNo,
                rowVersion: open.order.rowVersion,
                changes: { trackingCode },
              },
              {
                // Closes on success: the dialog was the one question, and the next row is waiting.
                onSuccess: () => {
                  toast(`${open.order.jobOrderNo} ${strings.shipping.trackingSaved}`);
                  close();
                },
                onError: (error: Error) => toast(failureWords(error)),
              },
            )
          }
        />
      )}

      {received.confirmation}

      {open?.kind === 'edit' && (
        <DispatchDialog
          mode="edit"
          jobOrderNo={open.order.jobOrderNo}
          shipping={open.order.dispatch}
          deliverTo={open.order.deliverTo}
          doctorName={open.order.dentistName}
          busy={updateShipping.isPending}
          onCancel={close}
          onSave={(changes) =>
            updateShipping.mutate(
              { jobOrderNo: open.order.jobOrderNo, rowVersion: open.order.rowVersion, changes },
              {
                onSuccess: () => {
                  toast(`${open.order.jobOrderNo} ${strings.shipping.editSaved}`);
                  close();
                },
                onError: (error: Error) => toast(failureWords(error)),
              },
            )
          }
        />
      )}

      {open?.kind === 'hold' && (
        <HoldDialog
          jobOrderNo={open.jobOrderNo}
          busy={hold.isPending}
          onCancel={close}
          onConfirm={(reason) =>
            hold.mutate(
              { jobOrderNo: open.jobOrderNo, reason },
              {
                onSuccess: () => {
                  toast(`${open.jobOrderNo} ${strings.payments.putOnHold}`);
                  close();
                },
                onError: (error: Error) => toast(failureWords(error)),
              },
            )
          }
        />
      )}
    </div>
  );
}
